/* hs-eslint ignored failing-rules */
/* eslint-disable react-hooks/exhaustive-deps */

'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import localeComparator from 'I18n/utils/localeComparator';
import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import * as FilterKey from '../../../constants/search/FilterKey';
import { OfficeLocationType } from '../../../constants/search/Filters';
import { translateFilterValue } from '../../../utils/i18n';
import { getCredentialById } from '../../../utils/credentials';
import { getCredentialDisplayName } from 'marketplace-ui-common/utils/credentials';
import { isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import FilterToggleGroup from 'marketplace-ui-common/filters/components/filterToggleGroup/FilterToggleGroup';
import * as Industries from 'marketplace-ui-common/constants/Industry';
import * as Countries from 'marketplace-ui-common/constants/Country';
import * as States from 'marketplace-ui-solutions-core/constants/USState';
import { SOLUTIONS_FILTERS_LANGUAGES } from 'marketplace-ui-common/constants/Languages';
import * as Budgets from 'marketplace-ui-solutions-core/constants/Budget';
import { getCertificationsKeyMirror, getCertifications, getCertificationsRequestState } from '../../../selectors/certifications';
import * as PartnerTiers from 'marketplace-ui-solutions-core/constants/PartnerTier';
import { sortTiers } from '../PartnerTierFilter';
import FilterDrilldownPanelWrapper from './FilterDrilldownPanelWrapper';
import { getAccreditations, getAccreditationsKeyMirror, getAccreditationsRequestState } from '../../../selectors/accreditations';
import { getIntegrationsKeyMirror, getIntegrationsRequestState } from '../../../slices/integrationsSlice';
import { getServices } from '../../../slices/serviceCatalogSlice';
import { jsx as _jsx } from "react/jsx-runtime";
const _PartnerTiers$NONE = PartnerTiers.NONE,
  FilterablePartnerTiers = _objectWithoutPropertiesLoose(PartnerTiers, [_PartnerTiers$NONE].map(_toPropertyKey));
const textPath = 'ecosystem-marketplace-solutions-ui.storefront.filters';
const ALL_VALUES = 'ALL_VALUES';
const sortAlphabetically = (selectedFilter, getName) => (valA, valB) => {
  if (getName) {
    return localeComparator(getName(valA), getName(valB));
  }
  return localeComparator(translateFilterValue(selectedFilter, valA), translateFilterValue(selectedFilter, valB));
};
const FilterDrilldownPanel = ({
  addFilter,
  applyFilters,
  onPanelClose,
  onRemoveFilter,
  filterType,
  multi,
  selectedValue
}) => {
  const lang = getLang();
  const certifications = useSelector(getCertifications);
  const accreditations = useSelector(getAccreditations);
  const accreditationsRequestState = useSelector(getAccreditationsRequestState);
  const Certifications = useSelector(getCertificationsKeyMirror);
  const certificationsRequestState = useSelector(getCertificationsRequestState);
  const Accreditations = useSelector(getAccreditationsKeyMirror);
  const integrationsRequestState = useSelector(getIntegrationsRequestState);
  const Integrations = useSelector(getIntegrationsKeyMirror);
  const services = useSelector(getServices);
  let sortFn;
  switch (filterType) {
    case FilterKey.TIER:
      sortFn = sortTiers;
      break;
    case FilterKey.ACCREDITATION:
      sortFn = sortAlphabetically(filterType, val => getCredentialDisplayName(getCredentialById(accreditations, val), lang));
      break;
    case FilterKey.CERTIFICATION:
      sortFn = sortAlphabetically(filterType, val => getCredentialDisplayName(getCredentialById(certifications, val), lang));
      break;
    case FilterKey.INTEGRATION:
      sortFn = sortAlphabetically(filterType, val => Integrations[val]);
      break;
    case FilterKey.SERVICE:
      sortFn = sortAlphabetically(filterType, val => {
        const serviceObject = services.filter(service => service.id === parseInt(val, 10))[0];
        return serviceObject && serviceObject.name;
      });
      break;
    default:
      sortFn = sortAlphabetically(filterType);
      break;
  }
  const filterKeyToEnumValueMap = useMemo(() => {
    return {
      [FilterKey.SERVICE]: services.reduce((obj, service) => {
        obj[service.id] = service.apiName;
        return obj;
      }, {}),
      [FilterKey.INDUSTRY]: Industries,
      [FilterKey.COUNTRY]: Countries,
      [FilterKey.STATE]: States,
      [FilterKey.LANGUAGE]: SOLUTIONS_FILTERS_LANGUAGES.reduce((result, language) => Object.assign({}, result, {
        [language]: language
      }), {}),
      [FilterKey.BUDGET]: Budgets,
      [FilterKey.CERTIFICATION]: Certifications,
      [FilterKey.ACCREDITATION]: Accreditations,
      [FilterKey.INTEGRATION]: Integrations,
      [FilterKey.TIER]: FilterablePartnerTiers,
      [FilterKey.OFFICE_LOCATION]: OfficeLocationType
    };
  }, [Certifications]);
  const valueMap = filterKeyToEnumValueMap[filterType];
  const getFilterTranslation = useCallback((filterKey, filterValue) => {
    if (filterKey === FilterKey.CERTIFICATION) {
      const certification = getCredentialById(certifications, filterValue);
      return certification && isSucceeded(certificationsRequestState) ? getCredentialDisplayName(certification, lang) : '';
    } else if (filterKey === FilterKey.ACCREDITATION) {
      const accreditation = getCredentialById(accreditations, filterValue);
      return accreditation && isSucceeded(accreditationsRequestState) ? getCredentialDisplayName(accreditation, lang) : '';
    } else if (filterKey === FilterKey.INTEGRATION) {
      return isSucceeded(integrationsRequestState) ? Integrations[filterValue] : '';
    } else if (filterKey === FilterKey.SERVICE) {
      const service = services && services.filter(serviceObject => serviceObject.id === parseInt(filterValue, 10))[0];
      return service && service.name;
    } else {
      return translateFilterValue(filterKey, filterValue);
    }
  }, [certifications, certificationsRequestState, lang]);
  const filterOptions = useMemo(() => {
    return Object.keys(valueMap).sort(sortFn).reduce((result, value) => {
      const translatedValue = getFilterTranslation(filterType, value);
      // weed out untranslated options
      if (filterType === FilterKey.SERVICE) {
        value = parseInt(value, 10);
      }
      return translatedValue ? [/* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
      ...result, {
        text: translatedValue,
        value
      }] : result;
    }, multi ? [] : [{
      text: unescapedText(`ecosystem-marketplace-solutions-ui.storefront.filters.${filterType}.placeholder`),
      value: ALL_VALUES
    }]);
  }, [filterType, valueMap]);
  return /*#__PURE__*/_jsx(FilterDrilldownPanelWrapper, {
    applyFilters: applyFilters,
    onPanelClose: onPanelClose,
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${textPath}.${filterType}.label`
    }),
    children: /*#__PURE__*/_jsx(FilterToggleGroup, {
      ariaLabel: unescapedText(`${textPath}.${filterType}.label`),
      inputClassName: "m-bottom-2",
      name: `${filterType}-toggle-group`,
      onChange: ({
        target: {
          value
        }
      }) => {
        if (value === ALL_VALUES) {
          onRemoveFilter(filterType);
        } else {
          addFilter(filterType, value);
        }
      },
      options: filterOptions,
      searchable: Object.keys(valueMap).length > 10,
      searchPlaceholder: unescapedText(`${textPath}.${filterType}.searchPlaceholder`),
      multi: multi,
      value: multi ? selectedValue : selectedValue || ALL_VALUES
    })
  });
};
export default FilterDrilldownPanel;